import { Core } from "@faire/design-tokens";
import { Chevron } from "@faire/web/slate/icons/foundation/Chevron";
import { Column, Row, HorizontalLine } from "@faire/web/slate/Layout";
import { Spacer, getSpacing } from "@faire/web/slate/spacing";
import { DefaultTheme } from "@faire/web/slate/Theme";
import { Span, Typography } from "@faire/web/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import React from "react";
import styled, { css } from "styled-components";

// Firefox increases the height of each list item by 1 to 1.5px
const SCROLL_HEIGHT_MARGIN_OF_ERROR = 1.1;

interface IProps {
  title: string;
  text?: string;
  maxNumberOfLinesShownWhenCollapsed: number;
  visibleComponent?: JSX.Element;
  visibleComponentInSpan?: JSX.Element;
  onExpandAccordionItem?: () => void;
  isListOfItems?: boolean;
  collapsedComponent?: JSX.Element;
  expandedComponent?: JSX.Element;
  isCustomizedCollapsableComponent?: boolean;
  onView?: () => void;
}

/* eslint-disable @faire/enforce-no-supersededby-components */
/**
 * @trackcomponent
 * @supersededBy "@faire/web/slate/Accordion/Accordion#Accordion"
 */
export const AccordionContainer: React.FunctionComponent<IProps> = ({
  title,
  maxNumberOfLinesShownWhenCollapsed,
  text,
  visibleComponent,
  visibleComponentInSpan,
  onExpandAccordionItem,
  isListOfItems,
  collapsedComponent,
  expandedComponent,
  isCustomizedCollapsableComponent,
  onView,
}) => {
  const viewRef = useDoActionWhenVisible(onView);
  const [isAccordionOpen, setIsAccordionOpen] = React.useState(false);
  const [isFullTextVisible, setIsFullTextVisible] =
    React.useState(isAccordionOpen);
  const [hasExpandableContent, setHasExpandableContent] = React.useState(true);
  const accordionContentRef = React.useRef<HTMLDivElement>(null);

  const maxHeightOfTextWhenCollapsed =
    maxNumberOfLinesShownWhenCollapsed *
    getLineHeight() *
    SCROLL_HEIGHT_MARGIN_OF_ERROR;
  const [textExpandedHeight, setTextExpandedHeight] = React.useState(
    maxHeightOfTextWhenCollapsed
  );

  React.useLayoutEffect(() => {
    if (!accordionContentRef.current) {
      return;
    }

    // The content height may change (for example when a product with a different description is selected)
    setTextExpandedHeight(accordionContentRef.current.scrollHeight);

    if (
      accordionContentRef.current.scrollHeight > maxHeightOfTextWhenCollapsed
    ) {
      setHasExpandableContent(true);
    } else if (
      accordionContentRef.current.scrollHeight <= maxHeightOfTextWhenCollapsed
    ) {
      setHasExpandableContent(false);
    }
  }, [maxHeightOfTextWhenCollapsed, accordionContentRef.current?.scrollHeight]);

  React.useEffect(() => {
    if (isAccordionOpen) {
      setIsFullTextVisible(true);
    }
  }, [isAccordionOpen]);

  const handleTransitionEnd = React.useCallback(() => {
    if (!isAccordionOpen) {
      setIsFullTextVisible(false);
    }
  }, [isAccordionOpen]);

  const toggleIsAccordionOpen = React.useCallback(() => {
    if (hasExpandableContent) {
      setIsAccordionOpen((prevState) => {
        if (!prevState) {
          onExpandAccordionItem?.();
        }
        return !prevState;
      });
    }
  }, [onExpandAccordionItem, hasExpandableContent]);

  return (
    <>
      <StyledAccordionContainer
        $hasExpandableContent={hasExpandableContent}
        onClick={toggleIsAccordionOpen}
        ref={onView ? viewRef : undefined}
      >
        <Column>
          <Typography variant="paragraphSansMedium">{title}</Typography>
          <Spacer size="1x" />
          {isCustomizedCollapsableComponent ? (
            <AccordionComponentSpan
              $isAccordionOpen={isAccordionOpen}
              $componentExpandedHeight={expandedComponent?.props?.height}
              $componentCollapsedHeight={collapsedComponent?.props?.height}
            >
              {isAccordionOpen ? expandedComponent : collapsedComponent}
            </AccordionComponentSpan>
          ) : (
            <AccordionTextSpan
              data-test-id="accordion-text"
              $isAccordionOpen={isAccordionOpen}
              $isFullTextVisible={isFullTextVisible}
              onTransitionEnd={handleTransitionEnd}
              ref={accordionContentRef}
              $maxNumberOfLinesShownWhenCollapsed={
                maxNumberOfLinesShownWhenCollapsed
              }
              $textExpandedHeight={textExpandedHeight}
              $textCollapsedHeight={maxHeightOfTextWhenCollapsed}
              $isListOfItems={isListOfItems}
            >
              {text}
              {visibleComponentInSpan}
            </AccordionTextSpan>
          )}

          {visibleComponent}
        </Column>
        {hasExpandableContent ? (
          <>
            <Spacer width="3x" />
            <ChevronRow align="flex-start">
              <AnimatedChevron $isAccordionOpen={isAccordionOpen}>
                <Chevron
                  data-test-id="accordion-chevron"
                  direction="right"
                  fontSize="16px"
                  color={Core.icon.default}
                />
              </AnimatedChevron>
            </ChevronRow>
          </>
        ) : null}
      </StyledAccordionContainer>
      <HorizontalLine />
    </>
  );
};

const StyledAccordionContainer = styled(Row)<{
  $hasExpandableContent: boolean;
}>`
  width: 100%;
  padding: ${getSpacing("3x", 0)};
  cursor: ${({ $hasExpandableContent }) =>
    $hasExpandableContent ? "pointer" : "unset"};
`;

const ACCORDION_TRANSITION_TIMING = `
  transition-duration: 0.25s;
  transition-timing-function: ease-out;
`;

const ChevronRow = styled(Row)`
  margin-left: auto;
  margin-right: 0;
`;

const AccordionComponentSpan = styled(Span)<{
  $isAccordionOpen: boolean;
  $componentExpandedHeight: number;
  $componentCollapsedHeight: number;
}>`
  text-align: start;
  word-break: break-word;
  white-space: pre-wrap;
  width: 100%;
  ${({
    $isAccordionOpen,
    $componentExpandedHeight,
    $componentCollapsedHeight,
  }) =>
    $isAccordionOpen
      ? css`
          max-height: ${$componentExpandedHeight}px;
        `
      : css`
          max-height: ${$componentCollapsedHeight}px;
        `}
  transition-property: max-height;
  ${ACCORDION_TRANSITION_TIMING}
`;

const AccordionTextSpan = styled(Span)<{
  $maxNumberOfLinesShownWhenCollapsed: number;
  $isAccordionOpen: boolean;
  $isFullTextVisible: boolean;
  $textExpandedHeight: number;
  $textCollapsedHeight: number;
  $isListOfItems?: boolean;
}>`
  text-align: start;
  word-break: break-word;
  white-space: pre-wrap;
  width: 100%;

  ${({ $isListOfItems }) =>
    $isListOfItems
      ? css`
          padding-left: ${getSpacing("1x")};
        `
      : ""}

  ${({ $isAccordionOpen, $textExpandedHeight, $textCollapsedHeight }) =>
    $isAccordionOpen
      ? css`
          max-height: ${$textExpandedHeight}px;
        `
      : css`
          max-height: ${$textCollapsedHeight}px;
        `}

  ${({ $isFullTextVisible, $maxNumberOfLinesShownWhenCollapsed }) =>
    !$isFullTextVisible
      ? css`
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: ${$maxNumberOfLinesShownWhenCollapsed};
        `
      : ""}

  text-overflow: ellipsis;
  overflow: hidden;

  transition-property: max-height;
  ${ACCORDION_TRANSITION_TIMING}
`;

const AnimatedChevron = styled.div<{ $isAccordionOpen: boolean }>`
  transform: rotate(
    ${({ $isAccordionOpen }) =>
      css`
        ${$isAccordionOpen ? -90 : 90}deg
      `}
  );
  transition-property: transform;
  ${ACCORDION_TRANSITION_TIMING}
`;

const DEFAULT_LINE_HEIGHT = "20px";

const getCSSLineHeight = (): string => {
  const lineHeightAttribute =
    DefaultTheme.typography.fonts.paragraphSansRegular["line-height"];

  return lineHeightAttribute?.toString() ?? DEFAULT_LINE_HEIGHT;
};

const getLineHeight = (): number => {
  const lineHeightFromCSSLineHeight = parseInt(getCSSLineHeight());

  return isNaN(lineHeightFromCSSLineHeight)
    ? parseInt(DEFAULT_LINE_HEIGHT)
    : lineHeightFromCSSLineHeight;
};
